import { getLocalTime } from '@qualle-admin/qutil/dist/date';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { uniqBy, sortBy } from 'lodash';
import router from '../../router';
import 'firebase/storage';
import moment from 'moment';
import { uid } from 'uid';

const state = {
  messages: [],
  unreadCount: 0,
  last_doc: null,
  users: [],
  error: null,
  companies: [],
  chats: [],
  loading: false,
  activeChatMessages: [],
  activeChat: null,
};
const mutations = {
  set_messages(state, payload) {
    state.messages = payload;
  },
  set_unread_count(state, payload) {
    state.unreadCount = payload;
  },
  set_last_doc(state, payload) {
    state.last_doc = payload;
  },
  reset_state(state) {
    state.messages = [];
  },
  set_users(state, payload) {
    state.users = payload;
  },
  update_error(state, payload) {
    state.error = payload;
  },
  set_companies(state, payload) {
    state.companies = payload;
  },
  add_companies(state, payload) {
    state.companies.push(payload);
  },
  set_chats(state, payload) {
    state.chats = payload;
  },
  set_loading(state, payload) {
    state.loading = payload;
  },
  set_active_chat_messages(state, payload) {
    state.activeChatMessages = payload;
  },
  set_active_chat(state, payload) {
    state.activeChat = payload;
  },
};
const actions = {
  // Storing messages into the chats collection
  create_message: async ({ commit, rootGetters }, payload) => {
    try {
      const data = JSON.parse(JSON.stringify(payload));
      const newMessageData = data.messageData;
      const reservationData = data.reservationData;
      const orgId = rootGetters['auth/get_user'].orgId;
      const userId = rootGetters['auth/get_user'].user_id;
      const userName = rootGetters['auth/get_user'].name;
      delete newMessageData.myself;
      const chatRef = await firebase
        .firestore()
        .collection('chats')
        .doc(newMessageData.reservationId);
      const participants = reservationData?.participants;
      var index =
        reservationData?.chatTitle === 'Reservation Chat'
          ? participants?.findIndex((ele) => ele?.orgId !== orgId)
          : participants.findIndex((ele) => ele !== orgId);

      chatRef.get().then((docSnapshot) => {
        if (docSnapshot.exists) {
          const { unreadCounts, users } = docSnapshot.data();

          if (unreadCounts) {
            reservationData.unreadCounts = { ...unreadCounts };
            reservationData.unreadCounts[participants[index]] =
              unreadCounts[participants[index]] + 1;

            if (!users.includes(userId)) {
              reservationData.users = [...users, userId];
            }
          } else {
            reservationData.participants[index].unreadCount =
              docSnapshot.data().participants[index]?.unreadCount + 1;

            if (users.findIndex((val) => val.id === userId) === -1) {
              reservationData.users = [
                ...users,
                {
                  id: userId,
                  name: userName,
                },
              ];
            }
          }
        } else {
          if (reservationData?.chatTitle === 'Reservation Chat') {
            reservationData.participants[index].unreadCount = 1;
            reservationData.users = [
              {
                id: userId,
                name: userName,
              },
            ];
          } else {
            reservationData.unreadCounts = {
              [reservationData.participants[index]]: 1,
              [orgId]: 0,
            };
            reservationData.users = [userId];
            reservationData.chatId = newMessageData.chatId;
          }
        }
        reservationData.lastMessage = {
          content: newMessageData.content,
          timestamp: newMessageData.timestamp,
        };
        reservationData.updatedAt = getLocalTime().format();
        chatRef.set(reservationData, { merge: true });
        chatRef.collection('messages').add(newMessageData);
      });
    } catch (error) {
      commit('update_error', error);
    }
  },

  // Getting latest messages from chats based on the reservation id
  get_new_messages: async ({ commit, dispatch }, payload) => {
    try {
      await firebase
        .firestore()
        .collection('chats')
        .doc(payload)
        .collection('messages')
        .orderBy('timestamp', 'asc')
        .limitToLast(30)
        .onSnapshot(({ docs }) => {
          if (docs.length) {
            commit('set_last_doc', docs[0]);
            const data = docs.map((val) => val.data());
            commit('set_messages', data);
            // dispatch('update_unread_count', payload);
          } else {
            commit('set_messages', []);
          }
        });
    } catch (error) {
      commit('update_error', error);
    }
  },

  // getting previous messages from the last message based on reservation Id
  get_prev_messages: async ({ commit, getters }, payload) => {
    try {
      await firebase
        .firestore()
        .collection('chats')
        .doc(payload.reservationId)
        .collection('messages')
        .orderBy('timestamp', 'asc')
        .endBefore(getters.get_last_doc)
        .limitToLast(30)
        .get()
        .then(async ({ docs }) => {
          await commit('set_last_doc', docs[0]);
          const messages = await docs.map((val) => val.data());
          const data = [...getters.get_messages];
          data.unshift(...messages);
          commit('set_messages', data);
        });
    } catch (error) {
      commit('update_error', error);
    }
  },

  // Getting all messages count
  get_end_message: async ({ commit }, payload) => {
    try {
      return firebase
        .firestore()
        .collection('chats')
        .doc(payload)
        .collection('messages')
        .orderBy('timestamp', 'asc')
        .limit(1)
        .get()
        .then(({ docs }) => {
          if (docs.length) {
            return docs[0].id;
          }
        });
    } catch (error) {
      commit('update_error', error);
    }
  },

  // Updating unread count to zero
  update_unread_count: async ({ commit, rootGetters }, payload) => {
    try {
      const orgId = rootGetters['auth/get_user'].orgId;
      const chatRef = await firebase
        .firestore()
        .collection('chats')
        .doc(payload);
      await chatRef.get().then(async (val) => {
        const data = val.data().unreadCounts;
        if (data) {
          chatRef.set({ unreadCounts: { [orgId]: 0 } }, { merge: true });
        } else {
          const data = val.data().participants;
          var index = data.findIndex((ele) => ele.orgId === orgId);
          data[index].unreadCount = 0;
          await chatRef.update({ participants: data });
        }
      });
    } catch (error) {
      commit('update_error', error);
    }
  },

  // Getting unread messages count from the chat
  get_unread_count: async ({ commit, dispatch, rootGetters }, payload) => {
    try {
      const orgId = rootGetters['auth/get_user'].orgId;
      await firebase
        .firestore()
        .collection('chats')
        .doc(payload)
        .onSnapshot(async (doc) => {
          if (doc.exists) {
            var unreadCounts = doc.data()?.unreadCounts;
            if (unreadCounts) {
              commit('set_unread_count', unreadCounts[orgId]);
              dispatch('set_users_data', doc.data()?.users || []);
            } else {
              var participantsData = doc.data().participants;
              if (participantsData) {
                var index = participantsData.findIndex(
                  (ele) => ele.orgId === orgId
                );
                commit('set_unread_count', participantsData[index].unreadCount);
                commit('set_users', doc.data()?.users || []);
              }
            }
          }
        });
    } catch (error) {
      commit('update_error', error);
    }
  },
  set_users_data: async ({ commit, rootGetters }, payload) => {
    const users = [];
    const userId = rootGetters['auth/get_user'].user_id;
    const userName = rootGetters['auth/get_user'].name;
    const test = payload.map(async (user, index) => {
      if (user !== userId) {
        const { name } = await (
          await firebase.firestore().collection('users').doc(user).get()
        ).data();
        users[index] = { name, id: user };
      } else {
        users[index] = { name: userName, id: user };
      }
    });
    await Promise.all(test);
    commit('set_users', users);
  },

  // Getitng committed exporters and reserved truckers to create chat
  getChatOrgs: async ({ commit, rootGetters, dispatch }) => {
    try {
      const orgId = rootGetters['auth/get_user'].orgId;
      if (!orgId) return;

      const commitmentsRef = firebase
        .firestore()
        .collection('commitments')
        .where('truckerOrgId', '==', orgId);
      const commitmentsSnapshot = await commitmentsRef.get();

      const exporterIds = commitmentsSnapshot.docs
        .map((doc) => {
          const approvedExporter = doc
            .data()
            .timeLine?.some(({ status }) => status === 'approved');
          return approvedExporter ? doc.data().orgId : null;
        })
        .filter(Boolean);

      const uniqueExporterIds = uniqBy(exporterIds);

      const exporters = await Promise.all(
        uniqueExporterIds.map(async (id) => {
          const orgData = await dispatch('getOrgData', id);
          return orgData
            ? { id, name: orgData.company, type: 'Shipper' }
            : null;
        })
      );

      const uniqueExporters = uniqBy(exporters.filter(Boolean), 'id');
      const reservationsRef = firebase.firestore().collection('reservations');
      const reservationsSnapshot = await reservationsRef.get();

      const truckerIds = reservationsSnapshot.docs
        .map((doc) => {
          if (doc.data().orgId === orgId) {
            const { requesterOrgId: id } = doc.data();

            return id;
          } else if (doc.data().requesterOrgId === orgId) {
            const { orgId: id } = doc.data();
            return id;
          }
          return null;
        })
        .filter(Boolean);

      const uniqIds = uniqBy(truckerIds);

      const uniqueTruckers = await Promise.all(
        uniqIds.map(async (id) => {
          const orgData = await dispatch('getOrgData', id);
          return orgData
            ? { id, name: orgData.company, type: 'Trucker' }
            : null;
        })
      );

      return [...uniqueExporters, ...uniqueTruckers];
    } catch (error) {
      commit('update_error', error);
    }
  },

  getChats: async ({ commit, dispatch, rootGetters, state }) => {
    try {
      const userOrgId = rootGetters['auth/get_user'].orgId;
      commit('set_loading', true);
      const queryByPartialId = firebase
        .firestore()
        .collection('chats')
        .where('participants', 'array-contains', userOrgId)
        .orderBy('lastMessage.timestamp', 'desc');
      queryByPartialId.onSnapshot(async (snapshot) => {
        const arr = [];
        await snapshot.docs.map(async (doc) => {
          const array = doc.data();
          array.participants.map(async (oId) => {
            if (
              oId !== userOrgId &&
              !state.companies.find((company) => company.orgId === oId)
            ) {
              const orgData = await dispatch('getOrgData', oId);
              commit('add_companies', {
                company: orgData?.company,
                orgId: oId,
                orgType: orgData?.org_type,
              });
            }
          });
          arr.push(array);
        });
        const { company, org_type: orgType } = rootGetters['auth/get_org'];
        if (!state.companies.find((company) => company.orgId === userOrgId)) {
          commit('add_companies', {
            company,
            orgId: userOrgId,
            orgType: orgType,
          });
        }

        commit('set_chats', arr);
      });
      commit('set_loading', false);
    } catch (error) {
      commit('update_error', error);
    }
  },
  getOrgData: async ({ commit }, payload) => {
    try {
      const docData = await firebase
        .firestore()
        .collection('organizations')
        .doc(payload)
        .get();
      return docData.data();
    } catch (error) {
      commit('update_error', error);
    }
  },

  getMessagesByChatId: async ({ commit, dispatch, rootGetters }, chatId) => {
    try {
      commit('set_loading', true);
      commit('set_active_chat_messages', []);
      const messageRef = firebase
        .firestore()
        .collection('chats')
        .doc(chatId)
        .collection('messages');
      messageRef.onSnapshot((snapshot) => {
        if (rootGetters['chats/get_active_chat']?.chatId === chatId) {
          const messages = snapshot.docs.map((doc) => doc.data());
          commit('set_active_chat_messages', sortBy(messages, 'timestamp'));
          dispatch('markAsRead', chatId);
        }
        commit('set_loading', false);
      });
    } catch (error) {
      commit('update_error', error);
    }
  },

  openChat: async ({ commit, dispatch, rootGetters }, chatId) => {
    const currentRoute = router.currentRoute;
    try {
      if (currentRoute.path === '/chat') {
        if (currentRoute.query.id !== chatId) {
          await router.replace({ query: { id: chatId } });
        }
      } else {
        await router.replace({ path: '/chat', query: { id: chatId } });
      }
    } catch (err) {
      if (err.name !== 'NavigationDuplicated') {
        throw err;
      }
    }
    const chat = rootGetters['chats/get_chats'].find(
      (i) => i.chatId === chatId
    );
    commit('set_active_chat', chat);
    await dispatch('getMessagesByChatId', chatId);
    await dispatch('getUsersByChatId', chat?.users);
  },
  getUsersByChatId: async ({ commit, dispatch, rootGetters }, usersData) => {
    const users = [];

    for (const user of usersData) {
      if (user !== rootGetters['auth/get_user'].user_id) {
        const avatar = (await dispatch('getUserPhoto', user)) || null;
        await dispatch('getUserById', { userId: user, avatar, users });
      }
    }

    const {
      user_id: userId,
      name,
      avatar,
      status,
      orgId,
    } = rootGetters['auth/get_user'];
    users.push({
      id: userId,
      orgId,
      name,
      avatar: avatar || null,
      status: status || 'online',
    });

    commit('set_users', users);
  },

  getUserById: ({ commit }, { userId, avatar, users }) => {
    try {
      const userRef = firebase.firestore().collection('users').doc(userId);
      userRef.onSnapshot((snapshot) => {
        const data = snapshot.data();
        const userIndex = users.findIndex((val) => val.id === snapshot.id);
        if (userIndex !== -1) {
          users[userIndex].status = data.status;
        } else {
          users.push({
            id: userId,
            orgId: data?.orgId,
            name: data?.name,
            avatar: avatar || null,
            status: data?.status || 'offline',
          });
        }
      });
    } catch (error) {
      commit('update_error', error);
    }
  },

  async getUserPhoto({ commit }, id) {
    const fileRef = firebase.storage().ref(`avatar/${id}`);

    try {
      const filesList = await fileRef.list();
      if (filesList.items.length > 0) {
        const firstFileRef = filesList.items[0];
        return await firstFileRef.getDownloadURL();
      } else {
        return '';
      }
    } catch (error) {
      commit('update_error', error);
    }
  },
  goToChat: async ({ commit, rootGetters, dispatch }, payload) => {
    try {
      const chatId = await dispatch('checkIfExist', payload);
      if (chatId) {
        await dispatch('openChat', chatId);
      } else {
        const Id = payload + '_' + rootGetters['auth/get_user'].orgId;
        await dispatch('createNewChat', { chatId: Id, orgId: payload });
      }
    } catch (error) {
      commit('update_error', error);
    }
  },

  checkIfExist: async ({ rootGetters }, payload) => {
    let chatId = payload + '_' + rootGetters['auth/get_user'].orgId;
    if (rootGetters['chats/get_chats'].some((c) => c.chatId === chatId)) {
      return chatId;
    } else {
      chatId = rootGetters['auth/get_user'].orgId + '_' + payload;
      if (rootGetters['chats/get_chats'].some((c) => c.chatId === chatId)) {
        return chatId;
      } else {
        return null;
      }
    }
  },

  createNewChat: async (
    { rootGetters, commit, dispatch },
    { chatId, orgId }
  ) => {
    const currentOrgId = rootGetters['auth/get_user'].orgId;
    const newChat = {
      chatId,
      participants: [currentOrgId, orgId],
      unreadCounts: { [currentOrgId]: 0, [orgId]: 0 },
      lastMessage: {
        content: '',
        timestamp: '',
      },
      users: [],
      updatedAt: '',
    };
    try {
      await firebase.firestore().collection('chats').doc(chatId).set(newChat);
      await dispatch('openChat', chatId);
    } catch (error) {
      commit('update_error', error);
    }
  },
  sendNewMessage: async (
    { dispatch, commit, rootGetters },
    { content, chatId, files, replyMessage }
  ) => {
    const newMessage = {
      id: uid(16),
      chatId: chatId,
      senderId: rootGetters['auth/get_user'].user_id,
      participantId: rootGetters['auth/get_user'].user_id,
      content,
      date: moment().format('MM/DD/YYYY'),
      timestamp: moment().format(),
      ...(replyMessage && { replyMessage }),
    };
    const fileUrls = [];
    try {
      if (files.length > 0) {
        commit('set_loading', true);
        await Promise.all(
          files.map(async (blob) => {
            const file = new File([blob.blob], blob.name, { type: blob.type });
            const fileRef = await firebase
              .storage()
              .ref(`chats/${chatId}/${newMessage.id}/${file.name}`);
            await fileRef.put(file);
            const url = await fileRef.getDownloadURL();
            const fileData = {
              localUrl: url,
              name: file.name,
              size: file.size,
              type: file.type,
            };
            fileUrls.push(fileData);
          })
        );
        newMessage.files = fileUrls;
      }
      const currentParticipant = rootGetters[
        'chats/get_active_chat'
      ]?.participants?.find((i) => i !== rootGetters['auth/get_user'].orgId);
      const chatData = {
        lastMessage: {
          content: newMessage.content,
          timestamp: moment().format(),
        },
        unreadCounts: {
          [currentParticipant]: firebase.firestore.FieldValue.increment(1),
        },
        updatedAt: moment().format(),
      };
      const { users } = rootGetters['chats/get_chats'].find(
        (c) => c.chatId === chatId
      );
      if (!users.includes(rootGetters['auth/get_user'].user_id)) {
        chatData.users = firebase.firestore.FieldValue.arrayUnion(
          rootGetters['auth/get_user'].user_id
        );
      }
      const chatDocRef = firebase.firestore().collection('chats').doc(chatId);
      await chatDocRef.set(chatData, { merge: true });
      const messageRef = chatDocRef.collection('messages');
      await messageRef.add(newMessage);
      commit('set_loading', false);
    } catch (error) {
      commit('update_error', error);
    }
  },

  // mark all message as read
  markAsRead: async ({ commit, rootGetters }, chatId) => {
    try {
      const orgId = rootGetters['auth/get_user'].orgId;
      const chatRef = firebase.firestore().collection('chats').doc(chatId);
      await chatRef.set({ unreadCounts: { [orgId]: 0 } }, { merge: true });
    } catch (error) {
      commit('update_error', error);
    }
  },
  downloadFileFromChat: async ({ commit, rootGetters }, file) => {
    const body = document.getElementsByClassName('styleChatWindow')[0];
    body.style.cursor = 'progress';
    try {
      const response = await fetch(file.localUrl);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', file.name);
      link.click();
      body.style.cursor = null;
    } catch (error) {
      body.style.cursor = null;
    }
  },
  updateParticipantArchivedData: async (
    { rootGetters, dispatch },
    { chatId, archived }
  ) => {
    try {
      const userOrgId = rootGetters['auth/get_user'].orgId;
      const chatRef = firebase.firestore().collection('chats').doc(chatId);
      const chatDoc = await chatRef.get();
      const chatData = chatDoc.data();

      const archivedData = chatData.archiveddata || {};

      archivedData[userOrgId] = archived;

      await chatRef.set(
        {
          archived: archivedData,
        },
        { merge: true }
      );
      const message = archived ? 'Chat archived' : 'Chat Unarchived';
      dispatch(
        'auth/new_notification',
        {
          type: 'notification',
          message: message,
        },
        { root: true }
      );
    } catch (error) {
      console.error('Error updating participant archived data:', error);
    }
  },
};
const getters = {
  get_messages(state) {
    return state.messages;
  },
  get_unreadcount(state) {
    return state.unreadCount;
  },
  get_last_doc(state) {
    return state.last_doc;
  },
  get_users(state) {
    return state.users;
  },
  get_error: (state) => state.error,
  get_companies(state) {
    return state.companies;
  },
  get_chats(state) {
    return state.chats;
  },
  get_loading(state) {
    return state.loading;
  },
  get_activeChatMessages(state) {
    return state.activeChatMessages;
  },
  get_active_chat(state) {
    return state.activeChat;
  },
};
export default {
  namespaced: true,
  actions,
  mutations,
  state,
  getters,
};
